import React, { memo, useEffect, useMemo, useState } from 'react';
import styles from './Services.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveSalonId, getActiveSalon } from '../../../Auth/store/selectors';
import { isDemoMode } from 'demo';
import { getServices } from '../../store/selectors';
import { salonManagementActions } from '../../store';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import { modalActions } from '../../../Modals/store';
import { List, arrayMove } from 'react-movable';
import { useTranslation } from 'react-i18next';
import { Ban, CheckCircle2, Plus, Scissors } from 'lucide-react';

import ConfirmDeleteModal from '../../../Common/ConfirmDeleteModal';
import { ServiceItem } from './containers/ServiceItem';

const FILTERS = [
  { id: 1, key: 'active', icon: CheckCircle2, tone: 'active' },
  { id: 2, key: 'disabled', icon: Ban, tone: 'disabled' },
];

const Services = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeFilterId, setActiveFilterId] = useState(1);
  const activeSalonId = useSelector(getActiveSalonId());
  const activeSalon = useSelector(getActiveSalon());
  const demoMode = isDemoMode(activeSalon);
  const { isMobile } = useWindowSize();
  const allServices = useSelector(getServices(0, false));
  const services = useSelector(getServices(activeFilterId, true));

  const colorsList = useMemo(
    () => [
      { name: t('services.colors.black'), value: '#1F1F1F' },
      { name: t('services.colors.blue'), value: '#35A8F6' },
      { name: t('services.colors.green'), value: '#2DC56A' },
      { name: t('services.colors.yellow'), value: '#FCB734' },
      { name: t('services.colors.peach'), value: '#FFB49D' },
      { name: t('services.colors.pink'), value: '#FF93C7' },
      { name: t('services.colors.lightBlue'), value: '#B5ACEF' },
    ],
    [t],
  );

  const stats = useMemo(
    () => ({
      // Attivi = tutti i non disabilitati (prenotabili + non prenotabili in app)
      1: allServices.filter((service) => !service.disabled).length,
      2: allServices.filter((service) => service.disabled).length,
    }),
    [allServices],
  );

  useEffect(() => {
    activeSalonId && dispatch(salonManagementActions.GET_SERVICES.REQUEST({}, null, { skipLoader: true }));
  }, [activeSalonId]);

  const openCreateServiceModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'createServiceModal',
      }),
    );
  };

  const addServiceButton = (
    <button type="button" className={styles.add_service_btn} onClick={openCreateServiceModal}>
      <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
      <span>{t('services.addService')}</span>
    </button>
  );

  return (
    <div
      className={clx(styles.services_container, {
        [styles.services_container_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('services.header.title')}
          description={t('services.header.description')}
        />
        {!isMobile && !demoMode && addServiceButton}
      </div>

      {isMobile && !demoMode ? <div className={styles.mobile_toolbar}>{addServiceButton}</div> : null}

      <div className={styles.list_card}>
        <div className={styles.filters_row}>
          {FILTERS.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilterId === filter.id;
            return (
              <button
                key={filter.id}
                type="button"
                className={clx(styles.filter_chip, styles[`filter_chip_${filter.tone}`], {
                  [styles.filter_chip_selected]: isActive,
                })}
                aria-pressed={isActive}
                onClick={() => setActiveFilterId(filter.id)}
              >
                <span className={styles.filter_chip_icon}>
                  <Icon size={16} strokeWidth={2.2} />
                </span>
                <span className={styles.filter_chip_copy}>
                  <span className={styles.filter_chip_name}>
                    {t(`services.filters.${filter.key}`)}
                  </span>
                  <span className={styles.filter_chip_value}>{stats[filter.id] ?? 0}</span>
                </span>
              </button>
            );
          })}
        </div>

        {services?.length ? (
          <ServicesList
            services={services}
            colorsList={colorsList}
            showAppOrder={activeFilterId === 1}
          />
        ) : (
          <div className={styles.empty_state}>
            <Scissors size={22} strokeWidth={2.2} aria-hidden="true" />
            <strong>{t('services.empty')}</strong>
            <span>{t('services.emptyHint')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ServicesList = memo(
  ({ services = [], colorsList = [], showAppOrder = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [serviceToDeleteId, setServiceToDeleteId] = useState(false);
    const [serviceToEnableId, setServiceToEnableId] = useState(false);
    const [items, setItems] = useState(services);
    const [openServiceId, setOpenServiceId] = useState(null);

    useEffect(() => {
      setItems(services);
    }, [services]);

    // Se il servizio aperto esce dal filtro corrente, chiudi la card (niente salto sul successivo).
    useEffect(() => {
      if (openServiceId == null) return;
      const stillVisible = services.some((service) => String(service.id) === String(openServiceId));
      if (!stillVisible) setOpenServiceId(null);
    }, [services, openServiceId]);

    useEffect(() => {
      if (items.length && services.length) {
        const changedServicesPosition = services.reduce((acc, service, index) => {
          if (+service.id === +items[index].id) return acc;

          const newPosition = items.findIndex(
            (serviceWithNewPosition) => +serviceWithNewPosition.id === +service.id,
          );
          const serviceNewPosition = {
            serviceId: +service.id,
            position: +newPosition + 1,
          };
          return [...acc, serviceNewPosition];
        }, []);

        if (changedServicesPosition.length) {
          dispatch(salonManagementActions.SET_SERVICES_POSITION.REQUEST(changedServicesPosition));
        }
      }
    }, [items]);

    const openEditServiceModal = (service) => {
      dispatch(
        modalActions.OPEN_MODAL.SUCCESS({
          type: 'editServiceModal',
          props: service,
        }),
      );
    };

    return (
      <>
        <ConfirmDeleteModal
          isModalOpen={serviceToDeleteId}
          setIsModalOpen={setServiceToDeleteId}
          title={t('services.confirmDelete.title')}
          buttons={[t('common.actions.cancel'), t('common.actions.disable')]}
          action={() =>
            dispatch(
              salonManagementActions.DELETE_SERVICE.REQUEST(
                {
                  idServizio: serviceToDeleteId,
                  disabilitato: 1,
                  setToAll: true,
                },
                () => setServiceToDeleteId(false),
              ),
            )
          }
        />
        <ConfirmDeleteModal
          isModalOpen={serviceToEnableId}
          setIsModalOpen={setServiceToEnableId}
          title={t('services.confirmEnable.title')}
          buttons={[t('common.actions.cancel'), t('common.actions.enable')]}
          buttonVariants={['default', 'primary']}
          Icon={null}
          action={() =>
            dispatch(
              salonManagementActions.ENABLE_SERVICE.REQUEST(
                {
                  idServizio: serviceToEnableId,
                  disabilitato: 0,
                  setToAll: true,
                },
                () => setServiceToEnableId(false),
              ),
            )
          }
        />
        <List
          values={items}
          onChange={({ oldIndex, newIndex }) => {
            const arrayMoved = arrayMove(services, oldIndex, newIndex);
            setItems(arrayMoved);
          }}
          renderList={({ children, props, isDragged }) => (
            <ul
              {...props}
              className={styles.services_list}
              style={{
                cursor: isDragged ? 'grabbing' : undefined,
              }}
            >
              {children}
            </ul>
          )}
          renderItem={({ value: service, props, isDragged, index }) => (
            <li
              {...props}
              className={styles.service_li}
              style={{
                ...props.style,
                listStyleType: 'none',
                cursor: isDragged ? 'grabbing' : 'inherit',
                position: isDragged ? 'fixed' : 'relative',
              }}
            >
              <ServiceItem
                colorsList={colorsList}
                openEditServiceModal={openEditServiceModal}
                service={service}
                setServiceToDeleteId={setServiceToDeleteId}
                setServiceToEnableId={setServiceToEnableId}
                isDragged={isDragged}
                isOpen={String(openServiceId) === String(service.id)}
                onOpenChange={(open) => setOpenServiceId(open ? service.id : null)}
                appPosition={showAppOrder ? index + 1 : null}
              />
            </li>
          )}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
  },
);

export default Services;
